import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { IDevice } from "@formant/core/model/IDevice";
import { Uuid } from "@formant/core/model/Uuid";

import { deviceClient } from "../../../clients";
import { authStore } from "../../../stores";
import { agentInstallScript } from "../../../utils/agentInstallScript";
import { Buttons } from "../../common/Buttons";
import { CopyCode } from "../../common/CopyCode";
import { Heading3 } from "../../common/Heading3";
import { Help } from "../../common/Help";
import { Popup } from "../../common/Popup";
import { StandardButton } from "../../common/StandardButton";
import * as styles from "./ReprovisionPopup.scss";

export interface IReprovisionPopupProps {
    device: IDevice | undefined;
    onClose: () => void;
}

@observer
export class ReprovisionPopup extends Component<IReprovisionPopupProps> {
    @observable
    private reprovisionToken: string | undefined = undefined;

    private reprovisionDevice = async (deviceId: Uuid) => {
        await deviceClient.unprovision(await authStore.token(), deviceId);
        const { token } = await deviceClient.generateProvisioningToken(
            await authStore.token(),
            deviceId,
        );
        return token;
    };

    public render() {
        const { device } = this.props;
        const { reprovisionToken } = this;

        const visible = device !== undefined;
        const loading = reprovisionToken === undefined;

        // if needs provisioning
        if (device !== undefined && reprovisionToken === undefined) {
            const deviceId = defined(device.id);
            this.reprovisionDevice(deviceId)
                .then(token => {
                    this.reprovisionToken = token;
                    return;
                })
                .catch(e => {
                    throw e;
                });
        }

        return (
            <Popup
                visible={visible}
                onClose={this.onDone}
                className={styles["reprovision-popup"]}
            >
                <Heading3 className={styles.heading}>
                    New Provision Token{" "}
                    <Help text="A provisioning token securely associates a robot with a device." />
                </Heading3>
                <div className={styles.content}>
                    <p>
                        Use the following provisioning token to bring your
                        device back to life. Copy and paste this line into your
                        device's terminal.
                    </p>
                    <CopyCode
                        singleLine={true}
                        value={
                            reprovisionToken
                                ? agentInstallScript(reprovisionToken)
                                : ""
                        }
                        className={styles.install}
                    />
                </div>
                <Buttons className={styles.actions}>
                    <StandardButton
                        primary={true}
                        className={styles.submit}
                        loading={loading}
                        onClick={this.onDone}
                    >
                        Done
                    </StandardButton>
                </Buttons>
            </Popup>
        );
    }

    private onDone = () => {
        this.reprovisionToken = undefined;
        this.props.onClose();
    };
}
